import { BaseApi } from '../apis/base.api';
import { handleData } from '../_services/response.handler';
import { isDefectTypesCatalogActive } from '../constants/defectTypesCatalog';
import {
  resolveDefectTypeParentId,
  resolveDefectTypeRowId
} from './defectTypeRestIdentity';

const STORAGE_KEY = 'solvit.defectTypes.master';
/** 저장 JSON 형식·클라이언트 해석 규칙이 바뀌면 올려 기존 localStorage 무시 (DB 스키마와 무관) */
const SCHEMA_VERSION = 3;

let initPromise = null;
let indexesBuiltWithData = false;
let allTypes = [];
let parentTypes = [];
const byId = new Map();
const childrenByParent = new Map();

function compareDefectTypeOrder(a, b) {
  const ao = a.viewOrder != null ? a.viewOrder : 999999;
  const bo = b.viewOrder != null ? b.viewOrder : 999999;
  if (ao !== bo) return ao - bo;
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
}

function normalizeParentKey(parentId) {
  const n = Number(parentId);
  return Number.isFinite(n) && n > 0 ? n : String(parentId);
}

function rebuildIndexes(types) {
  allTypes = Array.isArray(types) ? types.map((row) => ({ ...row })) : [];
  byId.clear();
  childrenByParent.clear();

  for (const t of allTypes) {
    const rowId = resolveDefectTypeRowId(t);
    if (rowId != null && (t.id == null || t.id === '')) {
      t.id = rowId;
    }
  }

  for (const t of allTypes) {
    if (t == null || t.id == null) continue;
    const num = Number(t.id);
    const key = Number.isFinite(num) ? num : t.id;
    byId.set(key, t);
    byId.set(String(t.id), t);
  }

  for (const t of allTypes) {
    const pid = resolveDefectTypeParentId(t);
    if (pid == null) continue;
    const pkey = normalizeParentKey(pid);
    if (!childrenByParent.has(pkey)) childrenByParent.set(pkey, []);
    childrenByParent.get(pkey).push(t);
  }

  for (const list of childrenByParent.values()) {
    list.sort(compareDefectTypeOrder);
  }

  parentTypes = allTypes
    .filter((t) => resolveDefectTypeParentId(t) == null)
    .sort(compareDefectTypeOrder);

  indexesBuiltWithData = allTypes.length > 0;
}

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.v !== SCHEMA_VERSION || !Array.isArray(parsed.defectTypes)) return null;
    return parsed.defectTypes;
  } catch {
    return null;
  }
}

function writeStorage(types) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        v: SCHEMA_VERSION,
        savedAt: Date.now(),
        defectTypes: types
      })
    );
  } catch (e) {
    console.warn('[defectTypesMasterCache] localStorage save failed:', e);
  }
}

/**
 * 결함 타입 마스터(전체 목록)를 localStorage 또는 GET /defectTypes 한 번으로 채운다.
 * 카탈로그 모드일 때는 no-op.
 */
export async function ensureDefectTypesMasterCache() {
  if (isDefectTypesCatalogActive()) return;
  if (indexesBuiltWithData) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const fromLs = readStorage();
      if (fromLs && fromLs.length > 0) {
        rebuildIndexes(fromLs);
        return;
      }
      const response = await BaseApi.get('/defectTypes');
      const data = handleData(response);
      const list = Array.isArray(data) ? data : data?._embedded?.defectTypes ?? [];
      if (list.length > 0) {
        writeStorage(list);
      }
      rebuildIndexes(list);
    } catch (e) {
      console.error('[defectTypesMasterCache] ensure failed:', e);
      rebuildIndexes([]);
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

export function prefetchDefectTypesMasterCache() {
  return ensureDefectTypesMasterCache().catch(() => {});
}

export function isDefectTypesMasterPopulated() {
  return indexesBuiltWithData;
}

export function getMasterParentTypes() {
  return parentTypes.slice();
}

export function getMasterAllTypes() {
  return allTypes.slice();
}

export function getMasterTypeById(defectTypeId) {
  if (defectTypeId == null || defectTypeId === '') return null;
  const n = Number(defectTypeId);
  return (
    byId.get(Number.isFinite(n) ? n : defectTypeId) ||
    byId.get(String(defectTypeId)) ||
    null
  );
}

export function getMasterChildren(parentId) {
  const key = normalizeParentKey(parentId);
  const list = childrenByParent.get(key);
  return list ? list.slice() : [];
}

/**
 * 스키마/데이터 갱신 시 캐시 무효화. SCHEMA_VERSION 올릴 때와 동일 효과를 위해 수동 호출 가능.
 */
export function invalidateDefectTypesMasterCache() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  indexesBuiltWithData = false;
  allTypes = [];
  parentTypes = [];
  byId.clear();
  childrenByParent.clear();
}

/**
 * GET /defectTypes 전체 등 평면 배열을 받아 localStorage·인덱스에 반영.
 */
export function ingestDefectTypesFlatList(types) {
  if (isDefectTypesCatalogActive()) return;
  if (!Array.isArray(types) || types.length === 0) return;
  writeStorage(types);
  rebuildIndexes(types);
}

/** HAL parent 링크 기준 루트 타입만 (findByParentIsNull 와 동일 의도) */
export function filterRootDefectTypesOnly(types) {
  if (!Array.isArray(types)) return [];
  return types.filter((t) => resolveDefectTypeParentId(t) == null).sort(compareDefectTypeOrder);
}

/**
 * 상위 타입 목록만 있을 때 자식 타입을 findByParent_Seq 로 모아 마스터를 채운다.
 */
export async function expandParentListIntoMasterCache(parentRows) {
  if (isDefectTypesCatalogActive()) return;
  if (!Array.isArray(parentRows) || parentRows.length === 0) return;

  const merged = [];
  const seen = new Set();

  function tryAdd(row) {
    if (row == null) return;
    const copy = { ...row };
    const id = resolveDefectTypeRowId(copy);
    if (id == null) return;
    if (seen.has(id)) return;
    seen.add(id);
    copy.id = id;
    merged.push(copy);
  }

  parentRows.forEach(tryAdd);

  for (const p of parentRows) {
    const pid = resolveDefectTypeRowId(p);
    if (pid == null) continue;
    try {
      const response = await BaseApi.get(
        `/defectTypes/search/findByParent_Seq?parent=${pid}`
      );
      const data = handleData(response);
      const kids = data?._embedded?.defectTypes ?? [];
      if (Array.isArray(kids)) {
        kids.forEach(tryAdd);
      }
    } catch (e) {
      console.warn('[defectTypesMasterCache] expand children failed parent=', pid, e);
    }
  }

  if (merged.length > 0) {
    writeStorage(merged);
    rebuildIndexes(merged);
  }
}

function snapshotTypesForMerge() {
  if (allTypes.length > 0) {
    return allTypes.map((t) => ({ ...t }));
  }
  const fromLs = readStorage();
  return fromLs && fromLs.length ? fromLs.map((t) => ({ ...t })) : [];
}

/**
 * 개별 API(자식 목록·단건) 응답을 기존 마스터에 합쳐 저장한다.
 */
export function mergeDefectTypesIntoMasterCache(incoming) {
  if (isDefectTypesCatalogActive()) return;
  if (incoming == null) return;
  const rows = Array.isArray(incoming) ? incoming : [incoming];
  if (rows.length === 0) return;

  const merged = snapshotTypesForMerge();
  const seen = new Set(
    merged.map((t) => resolveDefectTypeRowId(t)).filter((id) => id != null)
  );

  for (const row of rows) {
    if (row == null) continue;
    const copy = { ...row };
    const id = resolveDefectTypeRowId(copy);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    copy.id = id;
    merged.push(copy);
  }

  if (merged.length === 0) return;
  writeStorage(merged);
  rebuildIndexes(merged);
}
